import { useState } from 'react';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonInput,
  IonModal,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
  useIonToast,
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { useMutation } from '@apollo/client/react';
import { DISPLAY_NAME_MAX_LENGTH } from '../../../../appConfig';
import { UPDATE_USER_ACCOUNT } from '../../../../graphql/mutations/updateUserAccount';
import type {
  UpdateUserAccountMutation,
  UpdateUserAccountMutationVariables,
} from '../../../../types/graphql-generated';

export interface PersonalDataFields {
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  displayName?: string | null;
}

interface EditPersonalDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  userAccount: PersonalDataFields;
}

interface FormValues {
  firstName: string;
  lastName: string;
  displayName: string;
}

const initialValues = (userAccount: PersonalDataFields): FormValues => ({
  firstName: userAccount.firstName ?? '',
  lastName: userAccount.lastName ?? '',
  displayName: userAccount.displayName ?? '',
});

const fullNameFrom = ({ firstName, lastName }: Pick<FormValues, 'firstName' | 'lastName'>) =>
  [firstName.trim(), lastName.trim()].filter(Boolean).join(' ');

const REQUIRED_NAME_FIELDS: { field: 'firstName' | 'lastName'; message: string }[] = [
  { field: 'firstName', message: 'El nombre no puede quedar vacío' },
  { field: 'lastName', message: 'El apellido no puede quedar vacío' },
];

// Modal to edit the user's name fields from the ProfileTab.
export const EditPersonalDataModal = ({ isOpen, onClose, userAccount }: EditPersonalDataModalProps) => {
  const [values, setValues] = useState<FormValues>(() => initialValues(userAccount));
  const [errors, setErrors] = useState<string[]>([]);
  const [presentToast] = useIonToast();

  const [updateUserAccount, { loading: saving }] = useMutation<
    UpdateUserAccountMutation,
    UpdateUserAccountMutationVariables
  >(UPDATE_USER_ACCOUNT);

  const setValue = (field: keyof FormValues) => (value: string) =>
    setValues((current) => ({ ...current, [field]: value }));

  const handleWillPresent = () => {
    setValues(initialValues(userAccount));
    setErrors([]);
  };

  const handleSubmit = async () => {
    const trimmed: FormValues = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      displayName: values.displayName.trim(),
    };
    const fullName = fullNameFrom(trimmed);

    const validationErrors = REQUIRED_NAME_FIELDS.filter(({ field }) => !trimmed[field]).map(
      ({ message }) => message,
    );

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const { data } = await updateUserAccount({
        variables: {
          input: {
            firstName: trimmed.firstName || undefined,
            lastName: trimmed.lastName || undefined,
            fullName: fullName || undefined,
            // A blank display name clears it
            displayName: trimmed.displayName || null,
          },
        },
      });
      const result = data?.updateUserAccount;

      if (result?.userAccount && result.errors.length === 0) {
        presentToast({
          message: 'Datos personales actualizados',
          duration: 2500,
          position: 'bottom',
          positionAnchor: 'app-footer',
          cssClass: 'toast-success',
          buttons: [{ icon: closeOutline, role: 'cancel' }],
        });
        onClose();
      } else {
        setErrors([
          'No se han podido guardar los datos personales. Revisa los datos e inténtalo de nuevo.',
        ]);
      }
    } catch (error) {
      console.error('Error updating personal data:', error);
      setErrors(['No se han podido guardar los datos personales. Inténtalo de nuevo más tarde.']);
    }
  };

  const input = (
    field: keyof FormValues,
    label: string,
    extra: { maxlength?: number; placeholder?: string; required?: boolean } = {},
  ) => (
    <IonInput
      label={label}
      labelPlacement="floating"
      fill="outline"
      value={values[field]}
      onIonInput={(event) => setValue(field)(event.detail.value?.toString() ?? '')}
      aria-label={label}
      data-testid={`personal-data-${field}`}
      counter={extra.maxlength !== undefined}
      {...extra}
    />
  );

  return (
    <IonModal isOpen={isOpen} onWillPresent={handleWillPresent} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle className="ml-4">Datos personales</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose} disabled={saving}>
              Cancelar
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        <div className="space-y-4">
          {input('firstName', 'Nombre', { required: true })}
          {input('lastName', 'Apellido', { required: true })}
          <IonInput
            className="[--background:#f1f5f9]"
            label="Nombre completo"
            labelPlacement="floating"
            fill="outline"
            value={fullNameFrom(values)}
            disabled
            aria-label="Nombre completo"
            data-testid="personal-data-fullName"
          />
          <IonInput
            label="Conocido como"
            labelPlacement="stacked"
            fill="outline"
            value={values.displayName}
            onIonInput={(event) => setValue('displayName')(event.detail.value?.toString() ?? '')}
            placeholder="Ej: Juanín, Jony, González..."
            maxlength={DISPLAY_NAME_MAX_LENGTH}
            counter
            helperText="Si lo indicas, se mostrará en tu perfil en lugar de tu nombre completo."
            aria-label="Conocido como"
            data-testid="personal-data-displayName"
          />

          {errors.length > 0 && (
            <div className="rounded-xl border border-red-300 bg-red-50 p-3">
              {errors.map((error) => (
                <IonText color="danger" key={error}>
                  <p className="m-0 text-sm">{error}</p>
                </IonText>
              ))}
            </div>
          )}

          <IonButton expand="block" onClick={handleSubmit} disabled={saving}>
            {saving ? <IonSpinner name="crescent" /> : 'Guardar'}
          </IonButton>
        </div>
      </IonContent>
    </IonModal>
  );
};
